<script setup lang="ts">
import { computed, nextTick, ref, useId, watch } from 'vue'
import { IconChevronLeft, IconChevronRight, IconCalendar } from '@tabler/icons-vue'

const year = defineModel<number | undefined>()
const showYearInput = ref(false)
const yearInput = ref<HTMLInputElement | null>(null)
const yearInputId = useId()
async function toggleYearInput() {
  showYearInput.value = !showYearInput.value
  if (showYearInput.value) {
    await nextTick()
    yearInput.value?.focus()
  }
}
const emit = defineEmits<{ select: [] }>()
function selectYear(value: number) {
  year.value = value
  emit('select')
}
const decade = ref(Math.floor((year.value || new Date().getFullYear()) / 10) * 10)
const firstDecade = ref(Math.max(0, decade.value - 30))
const decades = computed(() => Array.from({ length: 4 }, (_, index) => firstDecade.value + index * 10).filter(value => value <= 9990))
const years = computed(() => Array.from({ length: 10 }, (_, index) => decade.value + index).filter(value => value >= 1 && value <= 9998))
function shiftDecades(offset: number) {
  firstDecade.value = Math.max(0, firstDecade.value + offset)
  decade.value = Math.min(9990, firstDecade.value + 30, Math.max(firstDecade.value, decade.value + offset))
}
watch(year, value => {
  if (!value || value < 1 || value > 9998) return
  decade.value = Math.floor(value / 10) * 10
  if (!decades.value.includes(decade.value)) firstDecade.value = Math.max(0, decade.value - 30)
})
</script>

<template>
  <div class="space-y-3" data-testid="year-picker">
    <div class="flex items-center justify-between gap-2">
      <span class="flex items-center gap-2 text-sm font-medium"><IconCalendar :size="18" aria-hidden="true" />Choose a year</span>
      <button type="button" class="btn btn-ghost btn-sm font-normal text-base-content/70" :aria-expanded="showYearInput" :aria-controls="yearInputId" @click="toggleYearInput">{{ showYearInput ? 'Use buttons' : 'Type a year' }}</button>
    </div>
    <label v-show="showYearInput" class="block text-sm">
      <span class="sr-only">Year</span>
      <input :id="yearInputId" ref="yearInput" v-model="year" type="number" min="1" max="9998" step="1" class="input w-full" aria-label="Year" />
    </label>
    <div v-show="!showYearInput" class="space-y-3">
      <div class="flex items-center gap-1 rounded-xl bg-base-200 p-1" role="group" aria-label="Choose a decade">
        <button type="button" class="btn btn-ghost btn-square h-11 w-10 shrink-0" aria-label="Earlier decades" :disabled="firstDecade === 0" @click="shiftDecades(-40)"><IconChevronLeft :size="18" aria-hidden="true" /></button>
        <div class="grid min-w-0 flex-1 grid-cols-4 gap-1">
          <button v-for="value in decades" :key="value" type="button" class="btn h-11 min-w-0 px-0 text-xs" :class="decade === value ? 'btn-primary' : 'btn-ghost'" :aria-pressed="decade === value" @click="decade = value">{{ value }}s</button>
        </div>
        <button type="button" class="btn btn-ghost btn-square h-11 w-10 shrink-0" aria-label="Later decades" :disabled="firstDecade + 40 > 9990" @click="shiftDecades(40)"><IconChevronRight :size="18" aria-hidden="true" /></button>
      </div>
      <div class="grid grid-cols-5 gap-2" role="group" aria-label="Choose a year">
        <button v-for="value in years" :key="value" type="button" class="btn h-11 px-1" :class="year === value ? 'btn-primary' : 'btn-outline'" :aria-pressed="year === value" @click="selectYear(value)">{{ value }}</button>
      </div>
    </div>
  </div>
</template>
